// The view-state behind the dock's TWO-STATE composer (owner, 11 Aug 2026): idle it is the input + Send,
// mid-run the same footprint carries the thinking row and its Stop. Both hooks exist because that swap
// UNMOUNTS one of the two children — so anything the reader would lose across it has to live out here,
// above the states, and the return has to be handed back deliberately rather than merely reappearing.
//
// Kept beside ChatConversation.tsx rather than inside it so that file stays under the size ceiling (G-085),
// the same reason the conversation was split from the dock in the first place.

import { useEffect, useRef, useState } from 'react';
import { useOptionalShell } from './ShellContext.tsx';

/**
 * The typed draft. In the real shell its single source of truth is the bounded per-conversation registry,
 * which outlives ChatDock's ordinary close/unmount. The local fallback keeps the reusable conversation
 * view renderable in isolated component tests; it is never used by the mounted dashboard.
 */
export function useChatDraft(): readonly [string, (value: string) => void] {
  const [isolatedDraft, setIsolatedDraft] = useState('');
  const shell = useOptionalShell();
  if (shell === null) return [isolatedDraft, setIsolatedDraft];
  return [shell.chatDraft, shell.setChatDraft];
}

/** When the run ends — stopped, finished, or failed — the composer returns with the caret already in it, so
 *  the reader carries straight on, and a screen reader hears the return that the departing `role="status"`
 *  row cannot announce for itself. Never on first paint: ONLY a run→idle change moves focus, because
 *  opening the dock must not take the page's focus away from wherever the reader put it. */
export function useComposerReturnFocus(
  thinking: boolean,
  input: React.RefObject<HTMLInputElement | null>,
): void {
  const wasThinking = useRef(false);
  useEffect(() => {
    if (wasThinking.current && !thinking) input.current?.focus();
    wasThinking.current = thinking;
  }, [thinking, input]);
}
